import React, { useEffect, useMemo, useState } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import './LocationManagers.css';
import './LocationRentalNotice.css';

interface Location {
  id: number;
  name: string;
  city: string;
  rental_notice_enabled?: boolean;
  rental_notice_text?: string | null;
  rental_notice_url?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

export interface RentalNotice {
  rental_notice_enabled: boolean;
  rental_notice_text: string | null;
  rental_notice_url: string | null;
}

interface LocationRentalNoticeProps {
  location: Location;
  /** Zodat het beheerpaneel de nieuwe waarden meteen meeneemt. */
  onSaved?: (notice: RentalNotice) => void;
}

const MAX_TEXT_LENGTH = 1000;
const MAX_URL_LENGTH = 500;

/** De tekst uit de afspraak, zodat niemand hem hoeft over te typen. */
const STANDARD_TEXT =
  'Voor het gebruik van de duofiets door zorgorganisaties of het gebruik van de duofiets ' +
  'voor cliënten buiten IJsselheem rekenen wij €5,- huurkosten.';

/**
 * Alleen een volledig https-adres mag door: de link komt op de openbare
 * reserveringspagina te staan, dus een `javascript:`-adres hoort er nooit in.
 * De server controleert dit ook.
 */
const isSafeUrl = (url: string): boolean => {
  if (!url) return true;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * De melding onder de privacymelding op het reserveringsformulier: dat er een
 * protocol voor de duofiets is en dat er huurkosten gelden voor zorgorganisaties
 * en cliënten van buiten IJsselheem. Per locatie in te stellen, omdat niet elke
 * locatie dezelfde afspraken heeft.
 */
const LocationRentalNotice: React.FC<LocationRentalNoticeProps> = ({ location, onSaved }) => {
  const [enabled, setEnabled] = useState<boolean>(location.rental_notice_enabled ?? false);
  const [text, setText] = useState<string>(location.rental_notice_text ?? '');
  const [url, setUrl] = useState<string>(location.rental_notice_url ?? '');
  const [saved, setSaved] = useState<RentalNotice>({
    rental_notice_enabled: location.rental_notice_enabled ?? false,
    rental_notice_text: location.rental_notice_text ?? null,
    rental_notice_url: location.rental_notice_url ?? null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Het overzicht in het beheerpaneel heeft deze velden niet altijd bij zich, dus
  // halen we de locatie er zelf bij als ze ontbreken.
  useEffect(() => {
    let cancelled = false;

    const apply = (notice: RentalNotice) => {
      setEnabled(notice.rental_notice_enabled);
      setText(notice.rental_notice_text ?? '');
      setUrl(notice.rental_notice_url ?? '');
      setSaved(notice);
    };

    const load = async () => {
      if (location.rental_notice_enabled !== undefined) {
        apply({
          rental_notice_enabled: location.rental_notice_enabled,
          rental_notice_text: location.rental_notice_text ?? null,
          rental_notice_url: location.rental_notice_url ?? null,
        });
        return;
      }

      setIsLoading(true);
      try {
        const response = await apiClient.get<ApiResponse<Location>>(`/locations/${location.id}`);
        if (cancelled) return;
        apply({
          rental_notice_enabled: response.data.data?.rental_notice_enabled ?? false,
          rental_notice_text: response.data.data?.rental_notice_text ?? null,
          rental_notice_url: response.data.data?.rental_notice_url ?? null,
        });
      } catch (err) {
        if (!cancelled) setError('De huidige instelling kon niet geladen worden.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    location.id,
    location.rental_notice_enabled,
    location.rental_notice_text,
    location.rental_notice_url,
  ]);

  const trimmedText = text.trim();
  const trimmedUrl = url.trim();
  const urlIsSafe = isSafeUrl(trimmedUrl);

  const isChanged =
    enabled !== saved.rental_notice_enabled ||
    (trimmedText || null) !== saved.rental_notice_text ||
    (trimmedUrl || null) !== saved.rental_notice_url;

  const validationError = useMemo(() => {
    if (!urlIsSafe) {
      return 'Vul een volledig webadres in dat begint met https://, bijvoorbeeld https://www.ijsselheem.nl/protocol-duofiets.';
    }
    if (enabled && !trimmedText && !trimmedUrl) {
      return 'Vul een tekst of een link in, of zet de melding uit.';
    }
    if (trimmedText.length > MAX_TEXT_LENGTH) {
      return `De tekst mag maximaal ${MAX_TEXT_LENGTH} tekens lang zijn.`;
    }
    return null;
  }, [enabled, trimmedText, trimmedUrl, urlIsSafe]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSuccess(null);

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    setError(null);

    const payload: RentalNotice = {
      rental_notice_enabled: enabled,
      rental_notice_text: trimmedText || null,
      rental_notice_url: trimmedUrl || null,
    };

    try {
      await apiClient.put<ApiResponse<Location>>(
        `/locations/${location.id}/rental-notice`,
        payload
      );
      setSaved(payload);
      setText(trimmedText);
      setUrl(trimmedUrl);
      setSuccess(
        enabled
          ? 'Opgeslagen. De melding staat nu onder de privacymelding bij het reserveren.'
          : 'Opgeslagen. De melding staat uit en is niet zichtbaar bij het reserveren.'
      );
      onSaved?.(payload);
    } catch (err) {
      const details = (err as { response?: { data?: { details?: string[] } } })?.response?.data
        ?.details;
      setError(
        permissionMessage(err) ||
          (Array.isArray(details) ? details.join(', ') : null) ||
          'De instelling kon niet opgeslagen worden.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="location-managers">
      <div className="managers-intro">
        <h2>Melding huurkosten en protocol bij {location.name}</h2>
        <p>
          Onder de privacymelding op het reserveringsformulier kunt u een extra melding tonen. Zo
          weet iemand die een duofiets huurt dat er een protocol is en dat er huurkosten gelden voor
          zorgorganisaties en voor cliënten van buiten IJsselheem. U bepaalt hier zelf de tekst en de
          link naar het protocol.
        </p>
      </div>

      {error && <div className="managers-message managers-message-error">{error}</div>}
      {success && <div className="managers-message managers-message-success">{success}</div>}

      {isLoading ? (
        <p className="managers-loading">Instelling laden...</p>
      ) : (
        <form className="manager-form rental-notice-form" onSubmit={save}>
          <label className="manager-toggle rental-notice-switch">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
              disabled={isSaving}
            />
            <span className="manager-toggle-track">
              <span className="manager-toggle-thumb" />
            </span>
            <span className="manager-toggle-label">Melding tonen op het reserveringsformulier</span>
          </label>

          <div className="manager-form-group rental-notice-field">
            <label htmlFor={`rental-notice-text-${location.id}`}>Tekst van de melding</label>
            <textarea
              id={`rental-notice-text-${location.id}`}
              className="rental-notice-textarea"
              rows={4}
              maxLength={MAX_TEXT_LENGTH}
              value={text}
              onChange={(event) => setText(event.target.value)}
              disabled={isSaving}
              placeholder="Bijvoorbeeld: er gelden huurkosten voor zorgorganisaties."
            />
            <div className="rental-notice-meta">
              <button
                type="button"
                className="btn btn-secondary rental-notice-standard"
                onClick={() => setText(STANDARD_TEXT)}
                disabled={isSaving || text.trim() === STANDARD_TEXT}
              >
                Standaardtekst invullen
              </button>
              <span className="rental-notice-counter">
                {text.length} van {MAX_TEXT_LENGTH} tekens
              </span>
            </div>
          </div>

          <div className="manager-form-group rental-notice-field">
            <label htmlFor={`rental-notice-url-${location.id}`}>Link naar het protocol</label>
            <input
              id={`rental-notice-url-${location.id}`}
              type="url"
              inputMode="url"
              maxLength={MAX_URL_LENGTH}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              disabled={isSaving}
              placeholder="https://www.ijsselheem.nl/protocol-duofiets"
            />
            <p className="manager-form-hint">
              Moet beginnen met <code>https://</code>. Laat het veld leeg als u alleen een tekst wilt
              tonen.
            </p>
          </div>

          <div className="rental-notice-preview">
            <p className="rental-notice-preview-title">Zo ziet de huurder het:</p>
            {enabled && (trimmedText || (trimmedUrl && urlIsSafe)) ? (
              <div className="rental-notice-preview-body">
                {trimmedText && <span>{trimmedText}</span>}
                {trimmedUrl && urlIsSafe && (
                  <>
                    {trimmedText ? ' ' : null}
                    <a href={trimmedUrl} target="_blank" rel="noopener noreferrer">
                      Bekijk het protocol voor de duofiets
                    </a>
                  </>
                )}
              </div>
            ) : (
              <p className="rental-notice-preview-empty">
                {enabled
                  ? 'Nog niets om te tonen: vul een tekst of een geldige link in.'
                  : 'De melding staat uit, dus de huurder ziet niets.'}
              </p>
            )}
          </div>

          <div className="rental-notice-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving || !isChanged || Boolean(validationError)}
            >
              {isSaving ? 'Bezig...' : 'Opslaan'}
            </button>
            {validationError && !error && (
              <span className="rental-notice-warning">{validationError}</span>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default LocationRentalNotice;
